import LocationCoverageReport from "@/pages/Reports/LocationCoverageReport/LocationCoverageReport";
import type { RouteConfig } from "@/types/routestypes";
import { lazy } from "react";

const ScheduleReport = lazy(() => import("@/pages/Reports/ScheduleReport"));
const OpportunitiesByTeam = lazy(() => import("@/pages/Reports/Opportunities/OpportunitiesByTeam"));
const OpportunitiesByStatus = lazy(() => import("@/pages/Reports/Opportunities/OpportunitiesByStatus"));
const TeamPerformance = lazy(() => import("@/pages/Reports/Opportunities/TeamPerformance"));
const ExpenseByTeam = lazy(() => import("@/pages/Reports/Expense/ExpenseByTeam"));
const ExpenseByCategory = lazy(() => import("@/pages/Reports/Expense/ExpenseByCategory"));
const ExpenseCoverage = lazy(() => import("@/pages/Reports/Expense/ExpenseCoverage"));
const ExpenseCoverageDetails = lazy(() => import("@/pages/Reports/Expense/ExpenseCoverageDetails"));
const WorkingHours = lazy(() => import("@/pages/Reports/WorkingHours/WorkingHours"));
const CustomerReportRoute = lazy(() => import("@/pages/Reports/CustomerReport/CustomerReportRoute"));
const CallLogs = lazy(() => import("@/pages/Reports/CustomerReport/CallLogs"));
const VisitReport = lazy(() => import("@/pages/Reports/CustomerReport/VisitReport"));
const VisitCalender = lazy(() => import("@/pages/Reports/CustomerReport/VisitCalender"));
const VisitFrequency = lazy(() => import("@/pages/Reports/CustomerReport/VisitFrequency"));
const CustomerByStage = lazy(() => import("@/pages/Reports/CustomerReport/CustomerByStage"));
const CustomerByStatus = lazy(() => import("@/pages/Reports/CustomerReport/CustomerByStatus"));
const Coverage = lazy(() => import("@/pages/Reports/CustomerReport/Coverage"));
const SalesByTeamReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByTeam/SalesByTeamReport"));
const SalesByCustomerReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByCustomer/SalesByCustomerReport"));
const SalesByProductReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByProduct/SalesByProductReport"));
const SalesCoverageReport = lazy(() => import("@/pages/Reports/SalesReport/SalesCoverage/SalesCoverageReport"));
const SalesCoverageDetail = lazy(() => import("@/pages/Reports/SalesReport/SalesCoverage/Components/SalesCoverageDetail"));
const TeamPerformanceDetails = lazy(() => import("@/pages/Reports/Opportunities/TeamPerformanceDetails"));
const OpportunityReport = lazy(() => import("@/pages/Reports/Opportunities/OpportunityReport"));
const SalesByReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByReport/SalesByReport"));
const BulkUploadSales = lazy(() => import("@/pages/Reports/SalesReport/SalesByReport/BulkUploadSales"));
const SalesBulkReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByReport/SalesBulkReport"));
const SalesBulkViewReport = lazy(() => import("@/pages/Reports/SalesReport/SalesByReport/SalesBulkViewReport"));
const SalesReportPage = lazy(() => import("@/pages/Reports/SalesReport/SalesReportPage"));
const AttendanceCoverageReport = lazy(() => import("@/pages/Reports/AttendanceReport/AttendanceCoverageReport"));
const AttendanceReportDetail = lazy(() => import("@/pages/Reports/AttendanceReport/AttendanceReportDetail"));

const UserReportModule = lazy(() => import("@/pages/Reports/UserReport/UserReportModule"));
const UserReport = lazy(() => import("@/pages/Reports/UserReport/UserReport"));
const UserReportData = lazy(() => import("@/pages/Reports/UserReport/UserReportData"));

const isTestEnv = import.meta.env.VITE_API_TARGET_URL === "https://test.fieldforceconnect.com/";

const ReportsRoutes: RouteConfig = {
    path: "reports",
    pageName: "Reports",
    sidebar: {
        show: true,
        icon: "/assets/media/reportLogo.svg",
    },
    permission: [
        "CustomerReportView",
        "ExpenseReportView",
        "AttendanceReportExport",
        "SalesReportView",
        "DealsReportView"
    ],
    children: {
        CUSTOMER_REPORTS: {
            path: "customer",
            pageName: "Customer",
            permission: ["CustomerReportView"],
            children: {
                CUSTOMER_ADDED: {
                    path: "added",
                    element: <CustomerReportRoute />,
                    pageName: "Customer Added",
                    sidebar: { show: true },
                },
                CALL_LOGS: {
                    path: "call-logs",
                    element: <CallLogs />,
                    pageName: "Call Logs",
                    sidebar: { show: true },
                },
                VISIT_REPORT: {
                    path: "visit-report",
                    element: <VisitReport />,
                    pageName: "Visit Report",
                    sidebar: { show: true },
                },
                VISIT_CALENDAR: {
                    path: "visit-calendar",
                    element: <VisitCalender />,
                    pageName: "Visit Calendar",
                    sidebar: { show: true },
                },
                VISIT_FREQUENCY: {
                    path: "visit-frequency",
                    element: <VisitFrequency />,
                    pageName: "Visit Frequency",
                    sidebar: { show: true },
                },
                LEADS_BY_STAGE: {
                    path: "lead-stage",
                    element: <CustomerByStage />,
                    pageName: "Customers By Stage",
                    sidebar: { show: true },
                },
                LEADS_BY_STATUS: {
                    path: "lead-status",
                    element: <CustomerByStatus />,
                    pageName: "Customers By Status",
                    sidebar: { show: true },
                },
                CUSTOMER_COVERAGE: {
                    path: "coverage",
                    element: <Coverage />,
                    pageName: "Coverage",
                    sidebar: { show: true },
                },
            },
        },
        OPPORTUNITIES: {
            path: "opportunities",
            element: <OpportunityReport />,
            pageName: "Opportunities",
            permission: ["DealsReportView"],
            children: {
                OPPORTUNITIES_BY_TEAM: {
                    path: "team",
                    element: <OpportunitiesByTeam />,
                    pageName: "Opportunities By Team",
                    sidebar: { show: true },
                },
                OPPORTUNITIES_BY_STATUS: {
                    path: "status",
                    element: <OpportunitiesByStatus />,
                    pageName: "Opportunities By Status",
                    sidebar: { show: true },
                },
                TEAM_PERFORMANCE: {
                    path: "team-performance",
                    element: <TeamPerformance />,
                    pageName: "Team Performance",
                    sidebar: { show: true },
                },
                TEAM_PERFORMACE_DETAILS: {
                    path: "team-performance/report/:id",
                    element: <TeamPerformanceDetails />,
                    pageName: "Team Performance",
                    sidebar: { show: false },
                },
            }
        },
        SALES_REPORTS: {
            path: "sales",
            pageName: "Sales",
            element: <SalesReportPage />,
            permission: ["SalesReportView"],
            children: {
                SALES_BY_TEAM: {
                    path: "team",
                    element: <SalesByTeamReport />,
                    pageName: "Sales By Team",
                    sidebar: { show: true },
                },
                SALES_BY_CUSTOMER: {
                    path: "customer",
                    element: <SalesByCustomerReport />,
                    pageName: "Sales By Customer",
                    sidebar: { show: true },
                },
                SALES_BY_PRODUCT: {
                    path: "product",
                    element: <SalesByProductReport />,
                    pageName: "Sales By Product",
                    sidebar: { show: true },
                },
                SALES_COVERAGE: {
                    path: "coverage",
                    element: <SalesCoverageReport />,
                    pageName: "Sales Coverage",
                    sidebar: { show: true },
                },
                SALES_COVERAGE_DETAIL: {
                    path: "coverage-detail",
                    element: <SalesCoverageDetail />,
                    pageName: "Sales Coverage Detail",
                    sidebar: { show: false },
                },
                ...(isTestEnv && {
                    SALES_BY_REPORT: {
                        path: "sales-reports",
                        element: <SalesByReport />,
                        pageName: "Sales By Report",
                        sidebar: { show: true },
                    },
                }),
                BULK_UPLOAD: {
                    path: "bulk-upload",
                    element: <BulkUploadSales />,
                    pageName: "Bulk Upload",
                    sidebar: {
                        show: false,
                        icon: "",
                    },
                },
                BULK_REPORT: {
                    path: "bulk-report",
                    element: <SalesBulkReport />,
                    pageName: "Bulk Report",
                    sidebar: {
                        show: false,
                        icon: "",
                    },
                },
                BULK_REPORT_VIEW: {
                    path: "bulk-report/view/:id",
                    element: <SalesBulkViewReport />,
                    pageName: "Bulk Report View",
                    sidebar: {
                        show: false,
                        icon: "",
                    },
                },
            },
        },
        EXPENSE_REPORTS: {
            path: "expense",
            pageName: "Expense",
            permission: ["ExpenseReportView"],
            children: {
                EXPENSE_BY_TEAM: {
                    path: "team",
                    element: <ExpenseByTeam />,
                    pageName: "Expense By Team",
                    sidebar: { show: true },
                },
                EXPENSE_BY_CATEGORY: {
                    path: "category",
                    element: <ExpenseByCategory />,
                    pageName: "Expense By Category",
                    sidebar: { show: true },
                },
                EXPENSE_COVERAGE: {
                    path: "coverage",
                    element: <ExpenseCoverage />,
                    pageName: "Expense Coverage",
                    sidebar: { show: true },
                },
                EXPENSE_COVERAGE_DETAILS: {
                    path: "coverage-details",
                    element: <ExpenseCoverageDetails />,
                    pageName: "Expense Coverage Details",
                    sidebar: { show: false },
                },
            },
        },
        ATTENDANCE_REPORTS: {
            path: "attendance",
            pageName: "Attendance",
            permission: ["AttendanceReportExport"],
            children: {
                WORKING_HOURS: {
                    path: "working-hours",
                    element: <WorkingHours />,
                    pageName: "Working Hours",
                    sidebar: { show: true },
                },
                ATTENDANCE_REPORT: {
                    path: "report",
                    pageName: "Attendance Report",
                    sidebar: { show: true },
                    permission: ["AttendanceReportExport"],
                },
                ATTENDANCE_COVERAGE: {
                    path: "attendance-coverage",
                    pageName: "Attendance Coverage",
                    sidebar: { show: true },
                    children: {
                        ATTENDANCE_REPORT_LIST: {
                            path: "",
                            element: <AttendanceCoverageReport />,
                            pageName: "Attendance Coverage",
                            sidebar: { show: false },
                        },
                        VIEW_ATTENDANCE_DETAIL: {
                            path: "view/:userId",
                            element: <AttendanceReportDetail />,
                            pageName: "Attendance Coverage",
                            sidebar: { show: false },
                        }
                    }
                },
            },
        },
        LOCATION_REPORTS: {
            path: "location",
            pageName: "Location",
            sidebar: { show: true },
            permission: ["LocationView"],
            children: {
                LOCATION_REPORT: {
                    path: "location",
                    pageName: "Location Report",
                    sidebar: { show: true },
                },
                LOCATION_COVERAGE_REPORT: {
                    path: "location-coverage",
                    element: <LocationCoverageReport />,
                    pageName: "Location Coverage",
                    sidebar: { show: true },
                }
            }
        },
        SCHEDULE_REPORT: {
            path: "schedule",
            element: <ScheduleReport />,
            pageName: "Schedule Report",
            sidebar: { show: true },
        },
        USER_REPORT: {
            path: "user-report",
            element: <UserReportModule />,
            pageName: "User Report",
            sidebar: { show: true, icon: "" },
            permission: ["UserReportView"],
            children: {
                MAIN: {
                    path: "",
                    pageName: "User Report",
                    element: <UserReport />,
                    sidebar: { show: false },
                },
                USER_REPORT_DATA: {
                    path: "data",
                    element: <UserReportData />,
                    pageName: "User Data",
                    sidebar: { show: false },
                }
            }
        },
    },
};

export default ReportsRoutes;